import React, { useState, useEffect, useRef } from "react";
import yaml from "js-yaml";
import BannerImage from "../ContentView/BannerImage";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal";
import ReCaptchaDemo from "../ReCaptchaDemo";
import BASE_URL from "../../config";

const CareerForm = ({ node, baseUrl }) => {
  const { title, bannerImage, jobPost } = node || {};
  const [expanded, setExpanded] = useState(null);
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [csrfToken, setCsrfToken] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resumeFileId, setResumeFileId] = useState(null);
  const [appliedPostName, setAppliedPostName] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");


  const navigate = useNavigate();

  const openModal = (postName) => {
    setAppliedPostName(postName);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token); // Save token when CAPTCHA is verified
  };

  const resetForm = () => {
    // Clear form fields
    document.querySelector("form").reset(); // Optionally, use React refs/state to manage fields
    setFormData({}); // Reset form values
    setResumeFileId(null); // Reset resume file ID
    // Reset ReCaptcha token
    setCaptchaToken(null);
  };

  const formConfig = `
    name:
      '#type': textfield
      '#title': Full Name
      '#placeholder': 'Enter Your Full Name'
      '#required': true
      '#required_error': 'This field is required'
    
    mobile:
      '#type': tel
      '#title': Mobile
      '#placeholder': 'Enter Your Mobile Number'
      '#required': true
      '#required_error': 'This field is required'

    email:
      '#type': email
      '#title': Email
      '#placeholder': 'Enter Your Email'
      '#required': true
      '#required_error': 'This field is required'

    applied_post_name:
      '#type': textarea
      '#title': 'Applied Post Name'
      '#hidden': true
      '#required': true
      '#required_error': 'This field is required'

    highest_qualification:
      '#type': textfield
      '#title': 'Highest Qualification '
      '#placeholder': 'Enter Your Highest Qualification  '
      '#required': true
      '#required_error': 'This field is required'

    total_work_experience:
      '#type': number
      '#title': 'Total Work Experience '
      '#placeholder': 'Enter Your Total Work Experience '
      '#required': true
      '#required_error': 'This field is required'
     
    upload_your_resume:
      '#type': document_file
      '#title': Upload Your Resume
      '#uri_scheme': public
      '#file_extensions': 'pdf,doc,docx'
      '#required': true
      '#required_error': 'This field is required'
  `;

  const parsedFormConfig = yaml.load(formConfig);
  const [error, setError] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = "auto"; // Enable scrolling
    }

    // Cleanup when the component unmounts or modal closes
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/session/token?_format=json`
        );
        if (!response.ok) throw new Error("Failed to fetch CSRF token");
        const token = await response.text();
        setCsrfToken(token);
      } catch (err) {
        console.error("CSRF Token Error:", err);
      }
    };
    fetchCsrfToken();
  }, []);

  const handleToggle = (index) => {
    setExpanded(expanded === index ? null : index);
  };

  const validateField = (name, value, type) => {
    const trimmedValue = value.trim();
    let error = "";

    if (!trimmedValue) {
      return "This field is required.";
    }

    if (type === "text") {
      if (trimmedValue.length < 2) {
        error = "Must be at least 2 characters.";
      } else if (!/^[A-Za-z\s.]+$/.test(trimmedValue)) {
        error = "Only letters, spaces, and periods are allowed!";
      }
    } else if (type === "tel") {
      const onlyDigitsRegex = /^\d*$/;

      if (!onlyDigitsRegex.test(trimmedValue)) {
        error = "Only numbers are allowed.";
      } else if (trimmedValue.length > 10) {
        error = "Phone number must be up to 10 digits!";
      } else if (trimmedValue.length > 0 && trimmedValue.length < 10) {
        error = "Phone number must be exactly 10 digits!";
      }
    } else if (type === "email") {
      // Allow only `@` and `.` as special characters
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      const specialCharCheck = /^[A-Za-z0-9@.]+$/;

      if (!emailRegex.test(trimmedValue)) {
        error = "Invalid email format!";
      } else if (!specialCharCheck.test(trimmedValue)) {
        error = "Email can only contain letters, numbers, '@', and '.'!";
      }
    } else if (type === "number") {
      const numberValue = Number(trimmedValue);
      if (isNaN(numberValue)) {
        error = "Invalid number!";
      } else if (numberValue < 0) {
        error = "Number cannot be negative!";
      }
    }

    return error;
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const error = validateField(name, value, type);

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name, value, type } = e.target;
    const error = validateField(name, value, type);

    setFormError((prev) => ({ ...prev, [name]: error }));
  };

  // const handleInputChange = (e) => {
  //   const { name, value, type } = e.target;
  //   let error = "";

  //   // General Trimmed Value
  //   const trimmedValue = value.trim();

  //   // Validation based on input type
  //   if (type === "text") {
  //     if (trimmedValue.length < 2) {
  //       error = "Must be at least 2 characters.";
  //     } else if (!/^[A-Za-z\s.]+$/.test(trimmedValue)) {
  //       error = "Only letters, spaces, and periods are allowed!";
  //     }
  //   } else if (type === "tel") {
  //     if (!/^\d{0,10}$/.test(trimmedValue)) {
  //       error = "Phone number must be up to 10 digits!";
  //     } else if (trimmedValue.length > 0 && trimmedValue.length < 10) {
  //       error = "Phone number must be exactly 10 digits!";
  //     }
  //   } else if (type === "email") {
  //     if (
  //       trimmedValue.length > 0 &&
  //       !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)
  //     ) {
  //       error = "Invalid email format!";
  //     }
  //   }

  //   // Update form data and errors
  //   setFormData((prev) => ({ ...prev, [name]: value }));
  //   setFormError((prev) => ({ ...prev, [name]: error }));
  // };



  // Function to handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Please upload a PDF or DOC file.");
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert("File size exceeds 5MB. Please upload a smaller file.");
      return;
    }

    // File upload URL
    const uploadPath = `${BASE_URL}/file/upload/node/resume_upload/field_upload?_format=json`;

    try {
      const response = await fetch(uploadPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "Content-Disposition": `file; filename="${file.name}"`,
          Authorization: "Basic c2hpc3NoYV9hZG1pbjpTaGlrc2hhaGNsQDIwMjQ=",
          "X-CSRF-Token": csrfToken,
        },
        body: file,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`File upload failed: ${response.status} - ${errorText}`);
        throw new Error(`HTTP error! Status: ${response.status}, Response: ${errorText}`);
      }

      const result = await response.json();
      const fileId = result.fid[0].value; // Extract file ID
      setResumeFileId(fileId); // Store file ID
      setUploadedFile(file); // Store uploaded file info for UI update

    } catch (error) {
      console.error("File upload error:", error);
      alert("File upload failed. Please check the upload URL and configuration.");
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      alert("Please complete the CAPTCHA.");
      return;
    }
    if (!resumeFileId) {
      alert("Please upload your resume before submitting the form.");
      return;
    }
    const { name, email, mobile } = formData;

    // Name Validation: Min 2 chars, only letters, periods, and at most one space (between)
    const trimmedName = name?.trim() || "";

    // Rule 1: Required
    if (!trimmedName) {
      alert("Name is required.");
      return;
    }

    // Rule 2: Min length
    if (trimmedName.length < 2) {
      alert("Name must be at least 2 characters long.");
      return;
    }

    // Rule 3: Leading/trailing space check
    if (name !== trimmedName) {
      alert("Name must not have leading or trailing spaces.");
      return;
    }

    // Rule 4: Multiple spaces between words
    if (/\s{2,}/.test(trimmedName)) {
      alert("In name only one space allowed between words .");
      return;
    }

    // Rule 5: Character check with regex
    const nameRegex = /^[a-zA-Z.]+(?: [a-zA-Z.]+)*$/;
    if (!nameRegex.test(trimmedName)) {
      alert("Name can only contain letters, periods, and single spaces between words.");
      return;
    }


    // Email Validation
    const trimmedEmail = email?.trim() || "";
    const emailFormatRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const allowedSpecialCharsRegex = /^[A-Za-z0-9@.]+$/;

    if (!trimmedEmail) {
      alert("Email is required.");
      return;
    }

    if (!emailFormatRegex.test(trimmedEmail)) {
      alert("Invalid email format. Please enter a valid email like example@domain.com.");
      return;
    }

    if (!allowedSpecialCharsRegex.test(trimmedEmail)) {
      alert("Email can only contain letters, numbers, '@', and '.'!");
      return;
    }

    // Phone Validation (optional if required)
    const trimmedMobile = mobile?.trim() || "";
    const onlyDigitsRegex = /^\d{10}$/;  // Ensures exactly 10 digits

    // Rule 1: Required
    if (!trimmedMobile) {
      alert("Phone number is required.");
      return;
    }

    // Rule 2: No spaces allowed (before, after, or within)
    if (/\s/.test(mobile)) {
      alert("Phone number must not contain any spaces.");
      return;
    }

    // Rule 3: Only digits allowed (exactly 10 digits)
    if (!onlyDigitsRegex.test(trimmedMobile)) {
      alert("Phone number must be exactly 10 digits and contain only numbers.");
      return;
    }

    // Rule 4: Too long
    if (trimmedMobile.length > 10) {
      alert("Phone number cannot exceed 10 digits.");
      return;
    }

    // Rule 5: Too short
    if (trimmedMobile.length < 10) {
      alert("Phone number must be exactly 10 digits.");
      return;
    }

    // total_work_experience Validation
    const experience = formData.total_work_experience?.trim();

    if (!experience) {
      alert("Total Work Experience is required.");
      return;
    }

    const experienceNumber = Number(experience);

    if (isNaN(experienceNumber)) {
      alert("Total Work Experience must be a valid number.");
      return;
    }

    if (experienceNumber < 0) {
      alert("Total Work Experience cannot be negative.");
      return;
    }

    if (!uploadedFile) {
      alert("Please upload your resume before submitting.");
      return;
    }

    const requestData = {
      webform_id: "career",
      upload_your_resume: resumeFileId,
      applied_post_name: appliedPostName,
      "g-recaptcha-response": captchaToken,
      ...formData,
    };

    setIsSubmitting(true);
    // console.log("Submitting form:", requestData);

    try {
      const response = await fetch(
        `${BASE_URL}/webform_rest/submit?_format=json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: "Basic c2hpc3NoYV9hZG1pbjpTaGlrc2hhaGNsQDIwMjQ=",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(requestData),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error Response:", errorText);
        throw new Error(
          `HTTP error! Status: ${response.status}, Response: ${errorText}`
        );
      }

      // const data = await response.json();
      // console.log("Submission Response:", data);

      setShowSuccessMessage(true); // Show success message
      alert("Form submitted successfully!");

      setFormData({}); // Reset form values
      setResumeFileId(null); // Reset resume file ID
      setUploadedFile(null); // Clear uploaded file from UI
      setCaptchaToken("");
      setIsModalOpen(false); // Close the modal after submission
      // Check if file input reference exists before resetting it
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      } else {
        console.warn("fileInputRef.current is undefined");
      }

      setTimeout(() => {
        setShowSuccessMessage(false);
        navigate("/careers"); // Redirect after message disappears
      }, 3000);
    } catch (err) {
      console.error("Submission Error:", err);
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormFields = () => {
    return Object.entries(parsedFormConfig).map(([key, field]) => {
      // Common Input Fields (Text, Email, Tel)
      if (["textfield", "email", "tel", "number"].includes(field["#type"])) {
        return (
          <div key={key} className="mb-1">
            <label htmlFor={key} className="block text-sm font-medium text-gray-900">
              {field["#title"]} <span className="text-red-500">*</span>
            </label>
            <input
              type={field["#type"]}
              id={key}
              name={key}
              placeholder={field["#placeholder"]}
              required={field["#required"]}
              className="mt-1 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 shadow-sm"
              value={formData[key] || ""}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onInvalid={(e) => {
                e.target.setCustomValidity(
                  field["#type"] === "email"
                    ? "Please enter a valid email address."
                    : field["#type"] === "tel"
                      ? "Please enter a valid telphone number."
                      : "This field is required."
                );
              }}
              onInput={(e) => e.target.setCustomValidity("")}
            />
            {formError[key] && <p className="text-red-500 text-xs mt-1">{formError[key]}</p>}
          </div>
        );
      }

      // File Upload Field with Drag & Drop Effect
      if (field["#type"] === "document_file") {
        return (
          <div key={key} className="mb-1">
            <label htmlFor={key} className="block text-sm font-medium text-gray-900">
              {field["#title"]} <span className="text-red-500">*</span>
            </label>

            <div
              className={`mt-1 flex items-center justify-center w-full border border-dashed ${uploadedFile ? "border-green-500 bg-green-50" : "border-gray-300 bg-gray-50"
                } rounded-lg p-4 cursor-pointer hover:bg-gray-100`}
            >
              <label htmlFor={key} className="cursor-pointer flex flex-col items-center">
                <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path>
                </svg>
                <span className="text-gray-600 text-sm mt-2">
                  {uploadedFile ? "File Uploaded Successfully" : "Click to upload or drag & drop"}
                </span>
                <span className="text-xs text-gray-500">Max: 5MB (DOC, DOCX, PDF)</span>
              </label>
              <input
                type="file"
                id={key}
                name={key}
                accept={field["#file_extensions"]}
                required={field["#required"]}
                className="hidden"
                onChange={handleFileChange}
                onInvalid={(e) => {
                  e.preventDefault(); // Prevent native tooltip
                  setFormError((prev) => ({
                    ...prev,
                    [key]: "Please upload a resume (DOC, DOCX, or PDF)."
                  }));
                }}
                onInput={(e) => {
                  setFormError((prev) => ({
                    ...prev,
                    [key]: ""
                  }));
                }}
              />
            </div>

            {/* Show uploaded file name */}
            {uploadedFile && (
              <p className="mt-2 text-sm text-green-600 font-semibold">
                Uploaded: {uploadedFile.name}
              </p>
            )}

            {formError[key] && <p className="text-red-500 text-xs mt-1">{formError[key]}</p>}
          </div>
        );
      }

      // Hidden Field for Applied Post Name
      if (key === "applied_post_name") {
        return (
          <div key={key}>
            <label htmlFor={key} className="block text-sm font-medium text-gray-900">
              {field["#title"]} <span className="text-red-500">*</span>
            </label>
            <input
              type={field["#type"]}
              id={key}
              name={key}
              placeholder={field["#placeholder"]}
              required={field["#required"]}
              className="mt-1 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 shadow-sm"
              value={appliedPostName}
              readOnly
            />
          </div>
        );
      }

      return null;
    });
  };

  return (
    <section>
      <div className="relative z-0">
        {bannerImage && bannerImage.url && (
          <BannerImage imageUrl={bannerImage.url} alt={bannerImage.alt || ""} />
        )}

        <div className="container mx-auto px-4 md:px-8 lg:px-12 xl:px-20 max-w-screen-xl">
          <h2 className="text-xl sm:text-xl lg:text-2xl font-bold uppercase text-indian-red font-heading text-center">
            {title}
          </h2>
          <div className="relative flex items-center justify-center line-with-image">
            <img
              src="/assets/SHIKSHA_Flower_SVG.svg"
              alt="headingflower"
              className="h-6 sm:h-6 lg:h-8 bg-white p-2"
            />
          </div>
          <section className="mt-6 sm:mt-8">
            {jobPost && jobPost.length > 0 ? (
              <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                {/* Desktop Table View */}
                <table className="w-full text-xs sm:text-sm text-left text-gray-500 hidden sm:table">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-2 sm:px-6 sm:py-3">Post Name</th>
                      <th scope="col" className="px-4 py-2 sm:px-6 sm:py-3">Positions</th>
                      <th scope="col" className="px-4 py-2 sm:px-6 sm:py-3">Location</th>
                      <th scope="col" className="px-4 py-2 sm:px-6 sm:py-3"><span className="sr-only">Details</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobPost.map((post, index) => (
                      <React.Fragment key={index}>
                        <tr className="bg-white border-b hover:bg-gray-50">
                          <th scope="row" className="px-4 py-2 sm:px-6 sm:py-4 font-medium text-gray-900 whitespace-nowrap">
                            {post.postName}
                          </th>
                          <td className="px-4 py-2 sm:px-6 sm:py-4">{post.positions}</td>
                          <td className="px-4 py-2 sm:px-6 sm:py-4">{post.location}</td>
                          <td className="px-4 py-2 sm:px-6 sm:py-4 text-right">
                            <button onClick={() => handleToggle(index)} className="font-medium text-burnt-umber hover:underline">
                              {expanded === index ? "Hide Details" : "View Details"}
                            </button>
                          </td>
                        </tr>
                        {expanded === index && (
                          <tr>
                            <td colSpan="4" className="px-4 py-2 sm:px-6 sm:py-4">
                              <div className="p-4 bg-gray-100 rounded-lg relative">
                                <div
                                  className="prose prose-sm max-w-none text-gray-800 [&_ul]:text-gray-800 [&_ol]:text-gray-800 [&_li]:marker:text-black"
                                  dangerouslySetInnerHTML={{ __html: post.description.value }}
                                />
                                <button
                                  onClick={() => openModal(post.postName)}
                                  className="mt-4 bg-warm-orange hover:underline text-white font-bold py-2 px-4 rounded"
                                >
                                  Apply Now
                                </button>
                                {/* <button
                                  onClick={closeModal}
                                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
                                >
                                  &#10005;
                                </button> */}
                              </div>


                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>

                {/* Mobile Card View */}
                <div className="sm:hidden">
                  {jobPost.map((post, index) => (
                    <div key={index} className="bg-white shadow-md rounded-lg mb-4 p-4">
                      <p className="text-lg font-medium text-gray-900">{post.postName}</p>
                      <p className="text-sm text-gray-600">Positions: {post.positions}</p>
                      <p className="text-sm text-gray-600">Location: {post.location}</p>
                      <button
                        onClick={() => handleToggle(index)}
                        className="mt-2 font-medium text-blue-600 hover:underline"
                      >
                        {expanded === index ? "Hide Details" : "View Details"}
                      </button>
                      {expanded === index && (
                        <div className="mt-2 p-4 bg-gray-100 rounded-lg">
                          <div
                            className="prose prose-sm max-w-none text-gray-800 [&_ul]:text-gray-800 [&_ol]:text-gray-800 [&_li]:marker:text-black"
                            dangerouslySetInnerHTML={{ __html: post.description.value }}
                          />
                          <button
                            onClick={() => openModal(post.postName)}
                            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
                          >
                            Apply Now for the post {post.postName}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

              </div>
            ) : (
              <p>No job posts available at the moment.</p>
            )}
          </section>

          {/* Modal for Applying */}
          {isModalOpen && (
            <Modal isOpen={isModalOpen} closeModal={closeModal}>
              <h3 className="text-base sm:text-lg font-medium  text-center ">Apply Now</h3>
              <form onSubmit={handleSubmit} novalidate className="mt-2 grid gap-0 sm:gap-2 sm:grid-cols-1 max-w-lg">
                {/* Render form fields */}
                {renderFormFields()}

                {/* ReCaptcha Component */}
                <ReCaptchaDemo onValidate={setCaptchaToken} />

                <div className="my-1 flex justify-end gap-4">
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-700 text-white font-bold my-4 mx-4 py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    onClick={() => {
                      resetForm();
                      closeModal();
                    }}
                  >
                    <span aria-hidden="true">&times;</span> Close
                  </button>
                  <button
                    type="submit"
                    className={`bg-blue-500 text-white font-bold my-4 mx-4 py-2 px-4 rounded focus:outline-none focus:shadow-outline ${captchaToken && !isSubmitting ? '' : 'opacity-50 cursor-not-allowed'}`}
                    disabled={!captchaToken || isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </Modal>
          )}

          {/* Success Message */}
          {showSuccessMessage && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
              <div className="bg-white p-4 sm:p-6 rounded shadow-md text-center max-w-md sm:max-w-md">
                <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-4">
                  Submission Successful!
                </h3>
                <p className="text-sm sm:text-base">
                  Your record has been submitted. We will get back to you soon.
                </p>
              </div>
            </div>
          )}

          {error && (
            <p className="text-red-500 mt-4 text-sm sm:text-base">
              {error.message}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default CareerForm;
